import { useState } from 'react'
import { Button } from '../components/button'
import { Input } from '../components/input'
import { Label } from '../components/label'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/card'
import { Alert, AlertDescription } from '../components/alert'
import { Badge } from '../components/badge'
import { US_STATES } from '../constants/states'
import { detectUserTimezone, COMMON_TIMEZONES } from '@joinomu/shared'

interface ProviderSignupFormProps {
  onSubmit: (data: {
    email: string
    password: string
    firstName: string
    lastName: string
    licenseNumber: string
    specialty: string
    phone: string
    licensed: string[]
    timezone: string
  }) => Promise<void>
  loading?: boolean
  error?: string
  showLoginLink?: boolean
  loginElement?: React.ReactNode
}

export function ProviderSignupForm({ 
  onSubmit, 
  loading = false, 
  error = '',
  showLoginLink = true,
  loginElement
}: ProviderSignupFormProps) {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    licenseNumber: '',
    specialty: '',
    phone: '',
    licensed: [] as string[],
    timezone: detectUserTimezone() // Auto-detect as default, user can change
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.email) newErrors.email = 'Email is required'
    if (!formData.password) newErrors.password = 'Password is required'
    if (formData.password.length < 8) newErrors.password = 'Password must be at least 8 characters'
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }
    if (!formData.firstName) newErrors.firstName = 'First name is required'
    if (!formData.lastName) newErrors.lastName = 'Last name is required'
    if (!formData.licenseNumber) newErrors.licenseNumber = 'License number is required'
    if (!formData.specialty) newErrors.specialty = 'Specialty is required'
    if (!formData.phone) newErrors.phone = 'Phone number is required'
    if (formData.licensed.length === 0) newErrors.licensed = 'At least one licensed state is required'
    if (!formData.timezone) newErrors.timezone = 'Timezone is required'

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm()) return

    const { confirmPassword, ...submitData } = formData
    await onSubmit(submitData)
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const handleStateToggle = (stateCode: string) => {
    setFormData(prev => ({
      ...prev,
      licensed: prev.licensed.includes(stateCode)
        ? prev.licensed.filter(s => s !== stateCode)
        : [...prev.licensed, stateCode]
    }))
    if (errors.licensed) {
      setErrors(prev => ({ ...prev, licensed: '' }))
    }
  }

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold text-center">Provider Registration</CardTitle>
        <CardDescription className="text-center">
          Create your healthcare provider account
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                type="text"
                placeholder="John"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                required
                disabled={loading}
              />
              {errors.firstName && (
                <p className="text-sm text-destructive">{errors.firstName}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                type="text"
                placeholder="Doe"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                required
                disabled={loading}
              />
              {errors.lastName && (
                <p className="text-sm text-destructive">{errors.lastName}</p>
              )}
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="provider@example.com"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              required
              disabled={loading}
            />
            {errors.email && (
              <p className="text-sm text-destructive">{errors.email}</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Minimum 8 characters"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                required
                disabled={loading}
              />
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Confirm your password"
                value={formData.confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                required
                disabled={loading}
              />
              {errors.confirmPassword && (
                <p className="text-sm text-destructive">{errors.confirmPassword}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="licenseNumber">License Number</Label>
              <Input
                id="licenseNumber"
                type="text"
                placeholder="MD123456"
                value={formData.licenseNumber}
                onChange={(e) => handleInputChange('licenseNumber', e.target.value)}
                required
                disabled={loading}
              />
              {errors.licenseNumber && (
                <p className="text-sm text-destructive">{errors.licenseNumber}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="specialty">Specialty</Label>
              <Input
                id="specialty"
                type="text"
                placeholder="Internal Medicine"
                value={formData.specialty}
                onChange={(e) => handleInputChange('specialty', e.target.value)}
                required
                disabled={loading}
              />
              {errors.specialty && (
                <p className="text-sm text-destructive">{errors.specialty}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              type="tel"
              placeholder="(555) 123-4567"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              required
              disabled={loading}
            />
            {errors.phone && (
              <p className="text-sm text-destructive">{errors.phone}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Licensed States</Label>
            <p className="text-sm text-muted-foreground">
              Select all states where you are licensed to practice medicine
            </p>
            <div className="border rounded-lg p-4 max-h-48 overflow-y-auto">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {US_STATES.map((state) => (
                  <div key={state.value} className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => handleStateToggle(state.value)}
                      disabled={loading}
                      className={`
                        flex items-center justify-center w-4 h-4 rounded border-2 transition-colors
                        ${formData.licensed.includes(state.value)
                          ? 'bg-primary border-primary text-primary-foreground'
                          : 'border-input hover:border-primary'
                        }
                        ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                      `}
                    >
                      {formData.licensed.includes(state.value) && (
                        <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                    <label 
                      className="text-sm cursor-pointer select-none"
                      onClick={() => !loading && handleStateToggle(state.value)}
                    >
                      {state.value} - {state.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            {formData.licensed.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {formData.licensed.map((stateCode) => {
                  const state = US_STATES.find(s => s.value === stateCode)
                  return (
                    <Badge
                      key={stateCode}
                      variant="secondary"
                      className="text-xs"
                    >
                      {state?.value}
                    </Badge>
                  )
                })}
              </div>
            )}
            {errors.licensed && (
              <p className="text-sm text-destructive">{errors.licensed}</p>
            )}
          </div>

          {/* Timezone Selection */}
          <div className="space-y-2">
            <Label>Timezone</Label>
            <p className="text-sm text-muted-foreground">
              Select your local timezone. This helps patients see appointment times correctly.
            </p>
            <select
              value={formData.timezone}
              onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
              disabled={loading}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {COMMON_TIMEZONES.map(tz => (
                <option key={tz.value} value={tz.value}>
                  {tz.label} ({tz.abbr})
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              Detected: {detectUserTimezone()}{formData.timezone !== detectUserTimezone() ? ' (Modified)' : ' (Auto-detected)'}
            </p>
            {errors.timezone && (
              <p className="text-sm text-destructive">{errors.timezone}</p>
            )}
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          <Button 
            type="submit" 
            className="w-full" 
            disabled={loading}
          >
            {loading ? 'Creating Account...' : 'Create Provider Account'}
          </Button>
        </form>
      </CardContent>
      {showLoginLink && loginElement && (
        <CardFooter className="flex justify-center">
          <p className="text-sm text-muted-foreground">
            Already have a provider account?{' '}
            {loginElement}
          </p>
        </CardFooter>
      )}
    </Card>
  )
}